import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PendingApproval } from '../api/pending-approvals.api';
import { pendingApprovalsQueryOptions } from '../api/queries';
import { TOOL_APPROVAL_QUERY_KEYS } from '../api/query-keys';

/**
 * The approvals a conversation's last turn paused on. Read while no turn streams, which is also
 * what reads them again when one ends: only a finished turn can have paused, and while one streams
 * the answer is about to change.
 */
const NONE: PendingApproval[] = [];

export function usePendingApprovals(contextId: string | null, busy: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    ...pendingApprovalsQueryOptions(contextId ?? ''),
    enabled: Boolean(contextId) && !busy,
  });

  /** Forgets the list once it has been answered, until the server is read again. */
  const clear = useCallback(() => {
    queryClient.setQueryData<PendingApproval[]>(
      TOOL_APPROVAL_QUERY_KEYS.pending(contextId ?? ''),
      [],
    );
  }, [queryClient, contextId]);

  return { approvals: busy ? NONE : (query.data ?? NONE), clear, refetch: query.refetch };
}
